import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Dropdown } from 'react-bootstrap';
import apinode from '../../services/apinode';
import { TrendingUp, Clock, Award, BarChart3, Calendar, Tv } from 'lucide-react';
import TVLiberacaoComponent from './TVLiberacaoComponent';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import './ProdutividadeComponent.css';

const getIntervaloAtual = () => {
  const agora = new Date();
  return `${String(agora.getHours()).padStart(2, '0')}:00`;
};

const getMinutosDecodosNaHoraAtual = () => new Date().getMinutes();

// ─── Tempo médio por pedido em minutos ─────────────────────────────────────
// tempoTrabalhadoMinutos / totalConferencias → ex: 2.3 min/pedido
const calcularTmpPorPedido = (tempoTrabalhadoMinutos, totalConferencias) => {
  if (!totalConferencias || totalConferencias === 0) return null;
  return (tempoTrabalhadoMinutos / totalConferencias).toFixed(1);
};

const formatarTmpPorPedido = (valor) => {
  if (valor === null) return '—';
  return `${valor} min`;
};

const ProdutividadeComponent = ({ conta }) => {
  const [produtividade, setProdutividade] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resumoPorConferente, setResumoPorConferente] = useState([]);
  const [erro, setErro] = useState(null);
  const [conferenteSelecionado, setConferenteSelecionado] = useState(null);
  const [dataInicio, setDataInicio] = useState(new Date().toISOString().split('T')[0]);
  const [dataFim, setDataFim] = useState(new Date().toISOString().split('T')[0]);
  const [modoTV, setModoTV] = useState(false);
  const [loadingInicial, setLoadingInicial] = useState(true);

  useEffect(() => {
    fetchProdutividade();
    const interval = setInterval(() => fetchProdutividade(true), 60000);
    return () => clearInterval(interval);
  }, [conta, dataInicio, dataFim]);

  useEffect(() => {
    if (resumoPorConferente.length > 0 && !conferenteSelecionado) {
      setConferenteSelecionado(resumoPorConferente[0].conferente);
    }
  }, [resumoPorConferente]);

  const fetchProdutividade = async (isRefresh = false) => {
    try {
      if (!isRefresh) setLoading(true);
      setErro(null);

      const idConta = conta || localStorage.idconta;
      const response = await apinode.get(
        `produtividade-conferente/${idConta}?dataInicio=${dataInicio}&dataFim=${dataFim}`
      );

      if (response.status !== 200) {
        throw new Error(`Erro na API: ${response.statusText}`);
      }

      let data = response.data;

      if (!Array.isArray(data)) {
        if (data.data && Array.isArray(data.data)) {
          data = data.data;
        } else if (data.result && Array.isArray(data.result)) {
          data = data.result;
        } else {
          data = [];
        }
      }

      setProdutividade(data);
      calcularResumo(data);
    } catch (error) {
      console.error('Erro ao buscar produtividade:', error);
      setErro(error.message);
      setProdutividade([]);
      setResumoPorConferente([]);
    } finally {
      setLoading(false);
      setLoadingInicial(false);
    }
  };

  const calcularResumo = (dados) => {
    if (!Array.isArray(dados) || dados.length === 0) {
      setResumoPorConferente([]);
      setConferenteSelecionado(null);
      return;
    }

    const hoje = new Date().toISOString().split('T')[0];
    const eHoje = dataFim === hoje;
    const intervaloAtualStr = getIntervaloAtual();
    const minutosPassados = getMinutosDecodosNaHoraAtual();

    const agrupado = dados.reduce((acc, item) => {
      const conferente = item && item.conferente ? item.conferente.trim() : 'Desconhecido';
      if (!acc[conferente]) {
        acc[conferente] = {
          conferente,
          totalConferencias: 0,
          tempoTrabalhadoTotal: 0,
          tempoOciosidadeTotal: 0,
          horasCom0Ociosidade: 0,
          maiorOciosidade: 0,
          horarios: []
        };
      }

      const tempoTrabalhado = item?.tempo_trabalhado_minutos || 0;
      const horaInicioItem = item?.intervalo_hora_inicio
        || (item?.intervalo_hora ? item.intervalo_hora.split(' ')[0] : null);
      const isAtual = eHoje && horaInicioItem === intervaloAtualStr;
      const ociosidadeCorrigida = isAtual
        ? Math.max(0, minutosPassados - tempoTrabalhado)
        : (item?.tempo_ociosidade_minutos || 0);

      acc[conferente].totalConferencias += item?.total_conferencias || 0;
      acc[conferente].tempoTrabalhadoTotal += tempoTrabalhado;
      acc[conferente].tempoOciosidadeTotal += ociosidadeCorrigida;
      acc[conferente].horarios.push(item?.intervalo_hora || '');

      if (ociosidadeCorrigida <= 5) acc[conferente].horasCom0Ociosidade += 1;
      if (ociosidadeCorrigida > acc[conferente].maiorOciosidade) acc[conferente].maiorOciosidade = ociosidadeCorrigida;

      return acc;
    }, {});

    const resumo = Object.values(agrupado)
      .map(item => {
        const totalTempo = item.tempoOciosidadeTotal + item.tempoTrabalhadoTotal;
        const horasTrabalhadas = item.tempoTrabalhadoTotal / 60;
        const uph = item.tempoTrabalhadoTotal >= 15
          ? (item.totalConferencias / horasTrabalhadas).toFixed(1)
          : null;
        const utilizacao = totalTempo > 0
          ? ((item.tempoTrabalhadoTotal / totalTempo) * 100).toFixed(1)
          : 0;

        // ── Tempo médio por pedido (segundos) ───────────────────────────
        const tmpPorPedido = calcularTmpPorPedido(item.tempoTrabalhadoTotal, item.totalConferencias);

        return {
          ...item,
          percentualProdutividade: utilizacao,
          percentualOciosidade: totalTempo > 0
            ? ((item.tempoOciosidadeTotal / totalTempo) * 100).toFixed(1)
            : 0,
          uph,
          baixaDemanda: item.tempoTrabalhadoTotal < 15,
          tmpPorPedido
        };
      })
      .sort((a, b) => parseFloat(b.percentualProdutividade) - parseFloat(a.percentualProdutividade));

    setResumoPorConferente(resumo);
    if (resumo.length > 0) setConferenteSelecionado(resumo[0].conferente);
  };

  const getStatusColor = (percentual) => {
    const valor = parseFloat(percentual || 0);
    if (valor >= 80) return { label: 'Excelente', color: '#51cf66' };
    if (valor >= 60) return { label: 'Bom', color: '#ffa500' };
    return { label: 'Atenção', color: '#ff6b6b' };
  };

  const formatarTempo = (minutos) => {
    const min = parseFloat(minutos || 0);
    if (min >= 60) return `${(min / 60).toFixed(1)}h`;
    return `${Math.round(min)}m`;
  };

  const isHoje = () => {
    const hoje = new Date().toISOString().split('T')[0];
    return dataFim === hoje;
  };

  const intervaloAtual = getIntervaloAtual();

  const dadosConferenteSelecionado = conferenteSelecionado
    ? produtividade.filter(
        item => item && item.conferente && item.conferente.trim() === conferenteSelecionado
      )
    : [];

  const dadosGraficoRanking = resumoPorConferente.slice(0, 10).map(conf => ({
    name: conf.conferente.substring(0, 15),
    utilizacao: parseFloat(conf.percentualProdutividade),
    conferencias: conf.totalConferencias
  }));

  const dadosGraficoLinha = (() => {
    if (produtividade.length === 0) return [];
    const horariosSet = new Set(produtividade.map(item => item?.intervalo_hora || '').filter(Boolean));
    const horarios = Array.from(horariosSet).sort();
    const minutosPassados = getMinutosDecodosNaHoraAtual();

    return horarios.map(hora => {
      const ponto = { hora };
      resumoPorConferente.forEach(conf => {
        const item = produtividade.find(d => d?.intervalo_hora === hora && d?.conferente?.trim() === conf.conferente);
        if (item) {
          const trabalhado = item.tempo_trabalhado_minutos || 0;
          const horaInicioItem = item?.intervalo_hora_inicio || (item?.intervalo_hora ? item.intervalo_hora.split(' ')[0] : null);
          const isAtual = isHoje() && horaInicioItem === intervaloAtual;
          const ocioso = isAtual ? Math.max(0, minutosPassados - trabalhado) : (item.tempo_ociosidade_minutos || 0);
          const total = trabalhado + ocioso;
          ponto[conf.conferente] = total > 0 ? parseFloat(((trabalhado / total) * 100).toFixed(0)) : 0;
        } else {
          ponto[conf.conferente] = null;
        }
      });
      return ponto;
    });
  })();

  const CORES_LINHAS = [
    '#51cf66', '#339af0', '#ffa500', '#ff6b6b', '#845ef7',
    '#20c997', '#f59f00', '#e64980', '#74c0fc', '#a9e34b'
  ];

  const TooltipLinha = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const horaInicioLabel = label ? label.split(' ')[0] : label;
    const isAtual = isHoje() && horaInicioLabel === intervaloAtual;
    return (
      <div style={{ backgroundColor: 'white', border: '1px solid #e9ecef', borderRadius: '8px', padding: '10px 14px', fontSize: '0.78rem', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <div style={{ fontWeight: 700, marginBottom: '6px', color: '#2d3748' }}>
          {label}
          {isAtual && (
            <span style={{ marginLeft: '6px', fontSize: '0.65rem', backgroundColor: '#fff3cd', color: '#856404', border: '1px solid #ffc107', borderRadius: '10px', padding: '1px 7px', fontWeight: 600 }}>
              ● em andamento
            </span>
          )}
        </div>
        {payload.map((entry, i) => (
          entry.value !== null && (
            <div key={i} style={{ color: entry.color, display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
              <span>{entry.name.split(' ')[0]}</span>
              <span style={{ fontWeight: 600 }}>{entry.value}%</span>
            </div>
          )
        ))}
      </div>
    );
  };

  if (loadingInicial) {
    return (
      <Container className="d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <div className="spinner-border mb-3" role="status"><span className="visually-hidden">Carregando...</span></div>
          <p className="text-muted">Carregando produtividade...</p>
        </div>
      </Container>
    );
  }

  if (erro) {
    return (
      <Container className="mt-4">
        <Card className="border-danger">
          <Card.Body><p className="text-danger mb-0">⚠️ Erro: {erro}</p></Card.Body>
        </Card>
      </Container>
    );
  }

  if (modoTV) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 9999, overflowY: 'auto' }}>
        <TVLiberacaoComponent conta={conta} />
        <button onClick={() => setModoTV(false)} style={{ position: 'fixed', top: '1rem', right: '1rem', zIndex: 10000, background: 'rgba(0,0,0,0.7)', border: '1px solid rgba(255,255,255,0.2)', color: 'white', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
          ✕ Sair da TV
        </button>
      </div>
    );
  }

  return (
    <div className="produtividade-container">
      <style>{`
        @media print {
          body { margin: 0; padding: 0; background: white; }
          .no-print { display: none !important; }
          .produtividade-container { width: 100%; background: white; }
          .produtividade-container .card { page-break-inside: avoid; border: 1px solid #ddd !important; box-shadow: none !important; }
          .container-fluid { width: 100%; margin: 0; padding: 20px; }
          .row { page-break-inside: avoid; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f5f5f5 !important; font-weight: bold; }
          .progress { display: none; }
          .produtividade-container .card-header { background-color: #f5f5f5 !important; }
          .list-group-item { border: 1px solid #ddd !important; }
        }
        @keyframes pulse-dot { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
        .intervalo-atual-row { background-color: #fffdf0 !important; }
        .intervalo-atual-row:hover { background-color: #fff9d6 !important; }
      `}</style>

      <Container fluid className="py-4">

        {/* ── Filtro de Data ── */}
        <Row className="mb-4 no-print">
          <Col md={12}>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label className="form-label fw-bold small" style={{ color: '#666', marginBottom: '0.5rem', display: 'block' }}>Data Início</label>
                <input type="date" className="form-control form-control-sm" value={dataInicio} onChange={e => setDataInicio(e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label className="form-label fw-bold small" style={{ color: '#666', marginBottom: '0.5rem', display: 'block' }}>Data Fim</label>
                <input type="date" className="form-control form-control-sm" value={dataFim} onChange={e => setDataFim(e.target.value)} />
              </div>
            </div>
          </Col>
        </Row>

        {/* ── Header ── */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex align-items-center gap-3 justify-content-between">
              <div className="d-flex align-items-center gap-3">
                <div style={{ backgroundColor: '#51cf66', padding: '0.8rem', borderRadius: '8px' }}>
                  <TrendingUp size={32} color="white" />
                </div>
                <div>
                  <h2 className="mb-1 fw-bold">Produtividade dos Conferentes</h2>
                  <p className="text-muted small mb-0">
                    {resumoPorConferente.length > 0
                      ? `Período: ${new Date(dataInicio).toLocaleDateString('pt-BR')} a ${new Date(dataFim).toLocaleDateString('pt-BR')}`
                      : 'Nenhum dado encontrado'}
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#51cf66' }}>{resumoPorConferente.length}</div>
                  <small style={{ color: '#666' }}>Conferentes</small>
                </div>
                <button
                  onClick={() => setModoTV(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#1a202c', border: '1px solid #2d3748', color: '#51cf66', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, transition: 'all 0.2s ease', whiteSpace: 'nowrap' }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#2d3748'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#1a202c'; }}
                >
                  <Tv size={16} /> Modo TV
                </button>
              </div>
            </div>
          </Col>
        </Row>

        {/* ── Legenda dos KPIs (compacta) ── */}
        <Row className="mb-4">
          <Col>
            <div style={{ backgroundColor: '#fff', border: '1px solid #e9ecef', borderRadius: '8px', padding: '0.5rem 1rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
              <small style={{ color: '#bbb', fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px', marginRight: '0.25rem' }}>Legenda:</small>

              {[
                { bg: '#e8f4fd', color: '#339af0', label: 'UPH', desc: 'Conf ÷ horas trabalhadas' },
                { bg: '#f0fdf4', color: '#51cf66', label: 'Utilização', desc: 'Trab ÷ presença' },
                { bg: '#f3f0ff', color: '#845ef7', label: 'T/pedido', desc: 'Tempo médio por conf' },
                { bg: '#fff8e6', color: '#ffa500', label: '⚠ pouca demanda', desc: '< 15min trabalhados' },
                { bg: '#fff3cd', color: '#856404', label: '● em andamento', desc: 'Hora atual parcial', border: '1px solid #ffc107' },
              ].map(item => (
                <div key={item.label} title={item.desc} style={{ display: 'flex', alignItems: 'center', gap: '4px', backgroundColor: item.bg, border: item.border || `1px solid ${item.color}22`, borderRadius: '20px', padding: '2px 10px', cursor: 'default' }}>
                  <span style={{ fontWeight: 700, color: item.color, fontSize: '0.72rem' }}>{item.label}</span>
                  <span style={{ color: '#999', fontSize: '0.68rem' }}>— {item.desc}</span>
                </div>
              ))}
            </div>
          </Col>
        </Row>

        {/* ── Sem Dados ── */}
        {resumoPorConferente.length === 0 ? (
          <Row className="mb-4">
            <Col>
              <Card className="text-center" style={{ padding: '3rem', backgroundColor: '#f9f9f9', border: '2px dashed #ddd' }}>
                <Calendar size={48} style={{ color: '#ccc', margin: '0 auto 1rem' }} />
                <h5 style={{ color: '#999' }}>Nenhum dado de produtividade encontrado</h5>
                <p style={{ color: '#bbb', marginBottom: 0 }}>Selecione um período com dados disponíveis</p>
              </Card>
            </Col>
          </Row>
        ) : (
          <>
            {/* ── Cards de Resumo ── */}
            <Row className="g-3 mb-4">
              {resumoPorConferente.map((conf, index) => {
                const status = getStatusColor(conf.percentualProdutividade);
                const isSelected = conferenteSelecionado === conf.conferente;
                return (
                  <Col xs={12} md={6} lg={4} key={index}>
                    <Card
                      onClick={() => setConferenteSelecionado(conf.conferente)}
                      style={{ cursor: 'pointer', borderLeft: `4px solid ${status.color}`, transition: 'all 0.3s ease', backgroundColor: isSelected ? '#fff' : '#fafafa', border: '1px solid #e0e0e0', outline: isSelected ? `2px solid ${status.color}` : 'none' }}
                      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.backgroundColor = 'white'; }}
                      onMouseLeave={e => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.transform = ''; e.currentTarget.style.backgroundColor = isSelected ? '#fff' : '#fafafa'; }}
                    >
                      <Card.Body style={{ padding: '1.2rem' }}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="mb-0 fw-semibold" style={{ fontSize: '0.95rem' }}>{conf.conferente}</h6>
                          <span className="badge" style={{ backgroundColor: status.color, color: 'white', fontSize: '0.7rem' }}>{status.label}</span>
                        </div>

                        {/* ── 3 KPIs principais: Utilização + UPH + T/pedido ── */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.6rem', marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>

                          {/* KPI 1 — Utilização */}
                          <div>
                            <small style={{ color: '#999', display: 'block', marginBottom: '0.2rem', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Utilização</small>
                            <strong style={{ color: status.color, fontSize: '1.4rem', lineHeight: 1 }}>{conf.percentualProdutividade}%</strong>
                            <div className="progress mt-1" style={{ height: '3px' }}>
                              <div className="progress-bar" style={{ width: `${conf.percentualProdutividade}%`, backgroundColor: status.color }} />
                            </div>
                            <small style={{ color: '#aaa', fontSize: '0.62rem' }}>trab / presença</small>
                          </div>

                          {/* KPI 2 — UPH */}
                          <div style={{ borderLeft: '1px solid #e9ecef', paddingLeft: '0.6rem' }}>
                            <small style={{ color: '#999', display: 'block', marginBottom: '0.2rem', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.4px' }}>UPH</small>
                            {conf.baixaDemanda ? (
                              <div>
                                <strong style={{ color: '#adb5bd', fontSize: '1.4rem', lineHeight: 1 }}>—</strong>
                                <div><small style={{ color: '#ffa500', fontSize: '0.62rem', fontWeight: 600 }}>⚠ pouca dem.</small></div>
                              </div>
                            ) : (
                              <div>
                                <strong style={{ color: '#339af0', fontSize: '1.4rem', lineHeight: 1 }}>{conf.uph}</strong>
                                <div><small style={{ color: '#aaa', fontSize: '0.62rem' }}>conf / hora</small></div>
                              </div>
                            )}
                          </div>

                          {/* ── KPI 3 — T/pedido (NOVO) ── */}
                          <div style={{ borderLeft: '1px solid #e9ecef', paddingLeft: '0.6rem' }}>
                            <small style={{ color: '#999', display: 'block', marginBottom: '0.2rem', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.4px' }}>T/pedido</small>
                            <strong style={{ color: '#845ef7', fontSize: '1.4rem', lineHeight: 1 }}>
                              {formatarTmpPorPedido(conf.tmpPorPedido)}
                            </strong>
                            <div><small style={{ color: '#aaa', fontSize: '0.62rem' }}>tempo médio</small></div>
                          </div>
                        </div>

                        {/* Métricas secundárias */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem', fontSize: '0.82rem' }}>
                          <div>
                            <small style={{ color: '#999', display: 'block', marginBottom: '0.1rem', fontSize: '0.68rem' }}>Conferências</small>
                            <strong style={{ color: '#212529' }}>{conf.totalConferencias}</strong>
                          </div>
                          <div>
                            <small style={{ color: '#51cf66', display: 'block', marginBottom: '0.1rem', fontSize: '0.68rem' }}>Trabalhado</small>
                            <strong style={{ color: '#51cf66' }}>{formatarTempo(conf.tempoTrabalhadoTotal)}</strong>
                          </div>
                          <div>
                            <small style={{ color: '#ff6b6b', display: 'block', marginBottom: '0.1rem', fontSize: '0.68rem' }}>Ocioso</small>
                            <strong style={{ color: '#ff6b6b' }}>{formatarTempo(conf.tempoOciosidadeTotal)}</strong>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>

            {/* ── Gráficos ── */}
            <Row className="g-3 mb-4">
              <Col lg={6}>
                <Card>
                  <Card.Header className="bg-white border-bottom py-3">
                    <div className="d-flex align-items-center gap-2">
                      <BarChart3 size={20} color="#51cf66" />
                      <h6 className="mb-0">Ranking — Utilização (%)</h6>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={dadosGraficoRanking}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                        <YAxis domain={[0, 100]} unit="%" />
                        <Tooltip formatter={v => [`${v}%`, 'Utilização']} />
                        <Bar dataKey="utilizacao" fill="#51cf66" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={6}>
                <Card>
                  <Card.Header className="bg-white border-bottom py-3">
                    <div className="d-flex align-items-center gap-2">
                      <TrendingUp size={20} color="#339af0" />
                      <h6 className="mb-0">
                        Utilização por Hora — Todos
                        {isHoje() && (
                          <span style={{ marginLeft: '8px', fontSize: '0.65rem', backgroundColor: '#fff3cd', color: '#856404', border: '1px solid #ffc107', borderRadius: '10px', padding: '2px 8px', fontWeight: 600, verticalAlign: 'middle' }}>
                            ● ao vivo
                          </span>
                        )}
                      </h6>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    {dadosGraficoLinha.length > 0 ? (
                      <>
                        <ResponsiveContainer width="100%" height={260}>
                          <LineChart data={dadosGraficoLinha}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="hora"
                              tick={({ x, y, payload }) => {
                                const horaInicio = payload.value ? payload.value.split(' ')[0] : payload.value;
                                const isAtual = isHoje() && horaInicio === intervaloAtual;
                                return (
                                  <text x={x} y={y + 10} textAnchor="middle" fill={isAtual ? '#856404' : '#666'} fontWeight={isAtual ? 700 : 400} fontSize={11}>
                                    {horaInicio}{isAtual ? ' ●' : ''}
                                  </text>
                                );
                              }}
                            />
                            <YAxis domain={[0, 100]} unit="%" />
                            <Tooltip content={<TooltipLinha />} />
                            {resumoPorConferente.map((conf, i) => (
                              <Line
                                key={conf.conferente}
                                type="monotone"
                                dataKey={conf.conferente}
                                stroke={CORES_LINHAS[i % CORES_LINHAS.length]}
                                strokeWidth={conferenteSelecionado === conf.conferente ? 3 : 1.5}
                                strokeOpacity={conferenteSelecionado === conf.conferente ? 1 : 0.4}
                                dot={(props) => {
                                  const { cx, cy, payload } = props;
                                  const horaInicio = payload.hora ? payload.hora.split(' ')[0] : payload.hora;
                                  if (isHoje() && horaInicio === intervaloAtual) {
                                    return <circle key={`dot-${conf.conferente}`} cx={cx} cy={cy} r={5} fill={CORES_LINHAS[i % CORES_LINHAS.length]} stroke="white" strokeWidth={2} />;
                                  }
                                  return <g key={`dot-${conf.conferente}-${cx}`} />;
                                }}
                                connectNulls
                              />
                            ))}
                          </LineChart>
                        </ResponsiveContainer>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.5rem' }}>
                          {resumoPorConferente.map((conf, i) => (
                            <div key={conf.conferente} onClick={() => setConferenteSelecionado(conf.conferente)}
                              style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', padding: '2px 8px', borderRadius: '12px', fontSize: '0.72rem', fontWeight: conferenteSelecionado === conf.conferente ? 700 : 400, backgroundColor: conferenteSelecionado === conf.conferente ? `${CORES_LINHAS[i % CORES_LINHAS.length]}22` : 'transparent', border: `1px solid ${CORES_LINHAS[i % CORES_LINHAS.length]}`, color: CORES_LINHAS[i % CORES_LINHAS.length], opacity: conferenteSelecionado === conf.conferente ? 1 : 0.65, transition: 'all 0.2s ease' }}>
                              <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: CORES_LINHAS[i % CORES_LINHAS.length], flexShrink: 0 }} />
                              {conf.conferente.split(' ')[0]}
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>Sem dados de evolução</div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* ── Top 5 ── */}
            <Row className="mb-4">
              <Col>
                <Card>
                  <Card.Header className="bg-white border-bottom py-3">
                    <div className="d-flex align-items-center gap-2">
                      <Award size={20} color="#845ef7" />
                      <h6 className="mb-0">Top 5 — Maior Utilização</h6>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div className="list-group list-group-flush">
                      {resumoPorConferente.slice(0, 5).map((conf, index) => {
                        const status = getStatusColor(conf.percentualProdutividade);
                        return (
                          <div key={index}
                            className="list-group-item d-flex align-items-center justify-content-between p-3"
                            style={{ cursor: 'pointer', backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'white' }}
                            onClick={() => setConferenteSelecionado(conf.conferente)}
                            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#efefef')}
                            onMouseLeave={e => (e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#f9f9f9' : 'white')}
                          >
                            <div className="d-flex align-items-center gap-3">
                              <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: status.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', fontSize: '18px', flexShrink: 0 }}>
                                {index + 1}
                              </div>
                              <div>
                                <h6 className="mb-0 fw-semibold">{conf.conferente}</h6>
                                <small className="text-muted">
                                  {conf.totalConferencias} conf
                                  {conf.uph ? ` · ${conf.uph} UPH` : ' · pouca demanda'}
                                  {' · '}{formatarTempo(conf.tempoTrabalhadoTotal)} trabalhado
                                </small>
                              </div>
                            </div>
                            <div className="text-end">
                              <h6 className="mb-0 fw-bold" style={{ color: status.color }}>{conf.percentualProdutividade}%</h6>
                              <small className="text-muted">utilização</small>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* ── Detalhes por Hora ── */}
            {conferenteSelecionado && (
              <Card className="mt-4">
                <Card.Header className="bg-white border-bottom">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 py-2">
                    <div className="d-flex align-items-center gap-2">
                      <Clock size={20} color="#51cf66" />
                      <h6 className="mb-0">Detalhes por Hora — {conferenteSelecionado}</h6>
                    </div>
                    <Dropdown className="no-print">
                      <Dropdown.Toggle variant="light" id="dropdown-conferente" className="border-1" style={{ minWidth: '200px' }}>
                        {conferenteSelecionado}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {resumoPorConferente.map(conf => (
                          <Dropdown.Item key={conf.conferente} onClick={() => setConferenteSelecionado(conf.conferente)} active={conferenteSelecionado === conf.conferente}>
                            {conf.conferente}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </Card.Header>

                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr className="border-bottom">
                          <th className="px-4 py-3 fw-semibold text-muted small">Horário</th>
                          <th className="px-4 py-3 fw-semibold text-muted small text-center">Conferências</th>
                          <th className="px-4 py-3 fw-semibold text-muted small text-center">UPH</th>
                          <th className="px-4 py-3 fw-semibold text-muted small text-center">Utilização</th>
                          <th className="px-4 py-3 fw-semibold text-muted small text-center">Trabalhado</th>
                          <th className="px-4 py-3 fw-semibold text-muted small text-center">Ocioso</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dadosConferenteSelecionado.length > 0 ? (
                          dadosConferenteSelecionado.map((item, index) => {
                            const tempoTrabalhado = item?.tempo_trabalhado_minutos || 0;
                            const horaInicioItem = item?.intervalo_hora_inicio || (item?.intervalo_hora ? item.intervalo_hora.split(' ')[0] : null);
                            const isIntervaloAtual = isHoje() && horaInicioItem === intervaloAtual;

                            const tempoOciosoRaw = item?.tempo_ociosidade_minutos || 0;
                            const tempoOcioso = isIntervaloAtual
                              ? Math.max(0, getMinutosDecodosNaHoraAtual() - tempoTrabalhado)
                              : tempoOciosoRaw;

                            const tempoTotal = tempoTrabalhado + tempoOcioso;
                            const utilizacaoIntervalo = tempoTotal > 0 ? ((tempoTrabalhado / tempoTotal) * 100).toFixed(0) : 0;
                            const status = getStatusColor(utilizacaoIntervalo);

                            const horasTrabalhadasIntervalo = tempoTrabalhado / 60;
                            const uphIntervalo = tempoTrabalhado >= 15
                              ? ((item?.total_conferencias || 0) / horasTrabalhadasIntervalo).toFixed(1)
                              : null;

                            // ── T/pedido do intervalo ────────────────────────
                            const tmpIntervalo = calcularTmpPorPedido(tempoTrabalhado, item?.total_conferencias);

                            return (
                              <tr key={index} className={`border-bottom align-middle${isIntervaloAtual ? ' intervalo-atual-row' : ''}`}>

                                <td className="px-4 py-3 fw-medium">
                                  {item?.intervalo_hora}
                                  {isIntervaloAtual && (
                                    <span style={{ marginLeft: '8px', fontSize: '0.62rem', backgroundColor: '#fff3cd', color: '#856404', border: '1px solid #ffc107', borderRadius: '10px', padding: '1px 8px', fontWeight: 600, verticalAlign: 'middle', animation: 'pulse-dot 2s ease-in-out infinite', display: 'inline-block' }}>
                                      ● em andamento
                                    </span>
                                  )}
                                </td>

                                <td className="px-4 py-3 text-center fw-bold">{item?.total_conferencias}</td>

                                {/* UPH */}
                                <td className="px-4 py-3 text-center">
                                  {uphIntervalo ? (
                                    <span style={{ fontWeight: 600, color: '#339af0', fontSize: '0.9rem' }}>{uphIntervalo}</span>
                                  ) : (
                                    <span title="Menos de 15min trabalhados" style={{ color: '#adb5bd', fontSize: '0.85rem', cursor: 'help' }}>—</span>
                                  )}
                                </td>

                                <td className="px-4 py-3 text-center">
                                  <span className="badge" style={{ backgroundColor: status.color, color: 'white' }}>{utilizacaoIntervalo}%</span>
                                </td>

                                <td className="px-4 py-3 text-center">
                                  <small className="d-block fw-semibold text-success mb-1">{formatarTempo(tempoTrabalhado)}</small>
                                  <div className="progress" style={{ height: '4px' }}>
                                    <div className="progress-bar bg-success" style={{ width: `${utilizacaoIntervalo}%` }} />
                                  </div>
                                </td>

                                <td className="px-4 py-3 text-center">
                                  <small className="d-block fw-semibold text-danger mb-1">{formatarTempo(tempoOcioso)}</small>
                                  <div className="progress" style={{ height: '4px' }}>
                                    <div className="progress-bar bg-danger" style={{ width: `${100 - utilizacaoIntervalo}%` }} />
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="6" className="px-4 py-5 text-center text-muted">Nenhum dado para este conferente</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card.Body>
              </Card>
            )}
          </>
        )}
      </Container>
    </div>
  );
};

export default ProdutividadeComponent;